import { financialYearOf, financialYearStart, type IsoDate, type IsoMonth, monthOf } from './calendar';

/**
 * Credit and debit notes (gap 4). A credit note takes back value or tax from a sale (s.34(1)): goods
 * returned, a discount agreed after the sale, a lower price, a short or faulty supply, or too much tax
 * charged. A debit note adds to it (s.34(3)): a higher price, or too little tax charged.
 *
 * A note says which invoices it's for (rule 53(1A)): one note may cover several invoices of the same
 * financial year. It goes where its invoice went, at the rate and place of supply the invoice charged.
 */

export const CREDIT_NOTE_REASONS = ['returned', 'discount', 'price_cut', 'deficient', 'overcharged'] as const;
export const DEBIT_NOTE_REASONS = ['price_up', 'undercharged'] as const;
export const NOTE_REASONS = [...CREDIT_NOTE_REASONS, ...DEBIT_NOTE_REASONS] as const;
export type NoteReason = (typeof NOTE_REASONS)[number];

export const isCreditReason = (reason: NoteReason) => (CREDIT_NOTE_REASONS as readonly string[]).includes(reason);

/**
 * The last day a credit note on a sale of `invoiceDate` can be reported: 30 November after the end of the
 * sale's financial year (s.34(2), since the Finance Act 2022; or the annual return's date, if earlier,
 * which EkApp doesn't know). Debit notes have no such limit.
 */
export function creditNoteDeadline(invoiceDate: IsoDate): IsoDate {
  const fy = financialYearOf(monthOf(invoiceDate));
  return `${Number(financialYearStart(fy).slice(0, 4)) + 1}-11-30`;
}

/** The last month whose return can carry a credit note on a sale of `invoiceDate`: November after its year. */
export function lastCreditNotePeriod(invoiceDate: IsoDate): IsoMonth {
  return creditNoteDeadline(invoiceDate).slice(0, 7);
}

/** Whether the invoices a note is for are all of one financial year, as one note must be. */
export function sameFinancialYear(dates: readonly IsoDate[]): boolean {
  return new Set(dates.map((date) => financialYearOf(monthOf(date)))).size <= 1;
}
